import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { todayISO } from "../lib/format";
import SymbolAutocomplete from "./SymbolAutocomplete";

const emptyForm = () => ({
  symbol: "",
  name: "",
  tx_type: "BUY",
  quantity: "",
  price: "",
  tx_date: todayISO(),
  notes: "",
});

export default function TransactionDialog({
  clientId,
  onCreated,
  transaction,           // If set → edit mode
  open: controlledOpen,  // Optional controlled state (used for edit)
  onOpenChange,
}) {
  const isEdit = !!transaction;
  const isControlled = controlledOpen !== undefined;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // Prefill / reset when opening
  useEffect(() => {
    if (!open) return;
    if (isEdit && transaction) {
      setForm({
        symbol: transaction.symbol || "",
        name: transaction.name || "",
        tx_type: transaction.tx_type || "BUY",
        quantity: String(transaction.quantity ?? ""),
        price: String(transaction.price ?? ""),
        tx_date: transaction.tx_date || todayISO(),
        notes: transaction.notes || "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, isEdit, transaction]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const fetchLtp = async () => {
    if (!form.symbol) {
      toast.error("Enter a symbol first");
      return;
    }
    setFetching(true);
    try {
      const { data } = await apiClient.get("/prices/quote", { params: { symbol: form.symbol } });
      set("price", data.price.toFixed(2));
      toast.success(`Loaded LTP for ${data.symbol}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quote not found");
    } finally {
      setFetching(false);
    }
  };

  const submit = async () => {
    if (!form.symbol || !form.quantity || !form.price || !form.tx_date) {
      toast.error("Fill required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        price: Number(form.price),
      };
      if (isEdit) {
        const { data } = await apiClient.put(
          `/advisor/clients/${clientId}/transactions/${transaction.id}`,
          payload
        );
        toast.success(`Updated ${data.tx_type} ${data.symbol}`);
      } else {
        const { data } = await apiClient.post(
          `/advisor/clients/${clientId}/transactions`,
          payload
        );
        toast.success(`${data.tx_type} ${data.symbol} recorded`);
      }
      setOpen(false);
      if (!isEdit) setForm(emptyForm());
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEdit ? "Failed to update" : "Failed to record"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!isControlled && (
        <button
          data-testid="add-transaction-btn"
          onClick={() => setOpen(true)}
          className="bg-lime hover:brightness-110 text-white px-3 sm:px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md flex items-center gap-1.5 sm:gap-2 active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          <span className="hidden sm:inline">Record Trade</span>
          <span className="sm:hidden">Trade</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-none">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {isEdit ? "Edit transaction" : "Record transaction"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-px bg-border border border-border mb-4">
            {["BUY", "SELL"].map((t) => (
              <button
                key={t}
                data-testid={`tx-type-${t}`}
                onClick={() => set("tx_type", t)}
                className={`p-2.5 text-xs font-medium uppercase tracking-wider ${
                  form.tx_type === t
                    ? t === "BUY"
                      ? "bg-profit text-white"
                      : "bg-loss text-white"
                    : "bg-card text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Symbol (NSE / BSE)
              </label>
              <div className="flex gap-2 mt-1">
                <div className="flex-1">
                  <SymbolAutocomplete
                    value={form.symbol}
                    onChange={(v) => set("symbol", v)}
                    onSelect={(item) => {
                      set("symbol", item.symbol);
                      if (!form.name) set("name", item.name);
                    }}
                    testId="tx-symbol"
                  />
                </div>
                <button
                  type="button"
                  data-testid="tx-fetch-ltp"
                  onClick={fetchLtp}
                  disabled={fetching}
                  className="border border-border hover:bg-secondary px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  LTP
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Type to search (e.g. RELI, INFY). NSE tickers default to .NS.
              </p>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Quantity
              </label>
              <input
                data-testid="tx-quantity"
                type="number"
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring tabular"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Price (₹)
              </label>
              <input
                data-testid="tx-price"
                type="number"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring tabular"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Trade date
              </label>
              <input
                data-testid="tx-date"
                type="date"
                value={form.tx_date}
                onChange={(e) => set("tx_date", e.target.value)}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Display name (optional)
              </label>
              <input
                data-testid="tx-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Reliance Industries"
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Notes
              </label>
              <textarea
                data-testid="tx-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <button
              onClick={() => setOpen(false)}
              className="border border-border hover:bg-secondary px-4 py-2 text-xs font-medium uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              data-testid="tx-submit"
              onClick={submit}
              disabled={loading}
              className={`text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md disabled:opacity-50 active:scale-[0.98] ${
                form.tx_type === "BUY" ? "bg-profit hover:brightness-110" : "bg-loss hover:brightness-110"
              }`}
            >
              {loading ? "Saving…" : isEdit ? `Update ${form.tx_type}` : `Save ${form.tx_type}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
